import type { LatestBehavioralPattern } from "@/lib/behavioralPatternLedger";
import type { GraphSnapshot } from "../../../supabase/functions/_shared/contextGraph";
import type {
  BehavioralThemeV2,
  ObservationClusterV2,
} from "../../../supabase/functions/_shared/bi-v2/contracts";
import {
  buildCoachVeeraBehavioralReadModel as buildCoachVeeraBehavioralReadModelState,
  toBehavioralLabel,
  type BehavioralConfidence,
  type BehavioralContradiction,
  type BehavioralEvidenceRef,
  type BehavioralPrimaryObservation,
  type BehavioralReadModel,
  type BehavioralSourceSummary,
  type BehavioralSupportSummary,
} from "@/lib/behavior/substrate";

type BuildCoachVeeraBehavioralReadModelParams = {
  biThemes?: BehavioralThemeV2[] | null;
  biClusters?: ObservationClusterV2[] | null;
  contextGraph?: GraphSnapshot | null;
  compatibilitySummary?: LatestBehavioralPattern | null;
};

export type CoachVeeraBehavioralEvidenceRef = BehavioralEvidenceRef;

export type CoachVeeraBehavioralPrimaryObservation = BehavioralPrimaryObservation;

export type CoachVeeraBehavioralContradiction = Omit<BehavioralContradiction, "reason"> & {
  reason: string;
};

export type CoachVeeraBehavioralSourceSummary = Omit<BehavioralSourceSummary, "graphEvidenceCount"> & {
  graphEvidenceCount: number | null;
};

export type CoachVeeraBehavioralReadModel = {
  primaryObservation: CoachVeeraBehavioralPrimaryObservation;
  supportingSignals: string[];
  confidence: BehavioralConfidence;
  contradictions: CoachVeeraBehavioralContradiction[];
  evidenceRefs: CoachVeeraBehavioralEvidenceRef[];
  sourceSummary: CoachVeeraBehavioralSourceSummary;
  supportSummary: BehavioralSupportSummary;
};

function formatPrimaryLabel(value: string): string {
  return toBehavioralLabel(value);
}

function buildSupportingSignals(
  supportSummary: BehavioralSupportSummary,
  sourceSummary: BehavioralSourceSummary,
  contradictions: BehavioralContradiction[],
): string[] {
  switch (supportSummary.kind) {
    case "canonical":
      return [
        `Supported by ${supportSummary.artifactCount} artifacts across ${supportSummary.meetingCount} meetings.`,
        contradictions.length === 0 && !supportSummary.thin
          ? "Recent evidence is aligned around this pattern."
          : null,
        contradictions.length > 0
          ? "Recent conflicting evidence is present."
          : null,
        sourceSummary.usedCompatibilityFallback
          ? "Compatibility fallback is supplementing sparse graph evidence."
          : null,
        supportSummary.thin
          ? "Evidence is still thin. Treat this as a working read, not a settled pattern."
          : null,
      ].filter((item): item is string => Boolean(item));
    case "compatibility_only_v1":
      return [
        `Compatibility-only fallback from ${supportSummary.legacyMeetingCount} legacy meetings.`,
      ];
    case "no_behavioral_data":
      return ["No canonical behavioral evidence is available yet."];
  }
}

function buildContradictionReason(contradiction: BehavioralContradiction): string {
  return contradiction.reason ?? `${formatPrimaryLabel(contradiction.behaviorKey)} has recent conflicting evidence.`;
}

function adaptState(state: BehavioralReadModel): CoachVeeraBehavioralReadModel {
  return {
    primaryObservation: {
      ...state.primaryObservation,
      label: formatPrimaryLabel(state.primaryObservation.label),
    },
    supportingSignals: buildSupportingSignals(state.supportSummary, state.sourceSummary, state.contradictions),
    confidence: state.confidence,
    contradictions: state.contradictions.map((contradiction) => ({
      ...contradiction,
      reason: buildContradictionReason(contradiction),
    })),
    evidenceRefs: state.evidenceRefs,
    sourceSummary: state.sourceSummary,
    supportSummary: state.supportSummary,
  };
}

export function buildCoachVeeraBehavioralReadModel(
  params: BuildCoachVeeraBehavioralReadModelParams,
): CoachVeeraBehavioralReadModel {
  return adaptState(buildCoachVeeraBehavioralReadModelState(params));
}

export type {
  BehavioralConfidence as CoachVeeraBehavioralConfidence,
  BehavioralObservationSource as CoachVeeraBehavioralObservationSource,
  BehavioralSourceMode as CoachVeeraBehavioralSourceMode,
} from "@/lib/behavior/substrate";
